import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { CandidateStatus, UpdateCandidateStatusDto } from '../../types/api';
import { getErrorMessage } from '../../utils/errorUtils';
import WeightedChip, { useGenericWeights } from '../Summary/WeightedChip';
import ErrorAlert from '../Common/ErrorAlert';
import LoadingSpinner from '../Common/LoadingSpinner';

interface CandidatesTableProps {
  cycleId: string;
}

const CandidatesTable: React.FC<CandidatesTableProps> = ({ cycleId }) => {
  const queryClient = useQueryClient();
  const { lang } = useDirection();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);
  const [formData, setFormData] = useState<UpdateCandidateStatusDto>({
    status: CandidateStatus.ACTIVE,
  });
  const [matamValue, setMatamValue] = useState<number | null>(null);

  const { data: cycleStatus, isLoading, error: queryError, refetch } = useQuery<any, Error>({
    queryKey: ['cycleStatus', cycleId],
    queryFn: () => apiService.getCycleStatus(cycleId),
    enabled: !!cycleId,
  });

  const assessWeights = useGenericWeights(cycleId);

  const { data: inactivationReasons } = useQuery({
    queryKey: ['inactivationReasons'],
    queryFn: () => apiService.getInactivationReasons(),
  });

  const updateCandidateStatusMutation = useMutation({
    mutationFn: ({ candidateId, ...data }: { candidateId: string } & UpdateCandidateStatusDto) =>
      apiService.updateCandidateStatus(candidateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycleStatus'] });
      refetch();
      handleCloseDialog();
    },
    onError: (error: any) => {
      setError(getErrorMessage(error));
    },
  });

  const uploadCandidatesCSVMutation = useMutation({
    mutationFn: ({ cycleId, file }: { cycleId: string; file: File }) =>
      apiService.uploadCandidatesCSV(cycleId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cycleStatus'] });
      refetch();
      setUploadSuccess(data.message);
      setError('');
    },
    onError: (error: any) => {
      setError(getErrorMessage(error));
      setUploadSuccess('');
    },
  });

  const handleOpenDialog = (candidate: any) => {
    setSelectedCandidate(candidate);
    setFormData({
      status: candidate.status,
      inactivationReason: candidate.inactivationReason,
      inactivationReasonText: candidate.inactivationReasonText,
    });
    setMatamValue(candidate.candidatePersonalMatam ?? null);
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCandidate(null);
    setMatamValue(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!selectedCandidate) return;

    if (formData.status === CandidateStatus.INACTIVE && !formData.inactivationReason) {
      setError(tr('cands.inactReasonRequired', lang));
      return;
    }

    updateCandidateStatusMutation.mutate({
      candidateId: selectedCandidate.id,
      ...formData,
      candidatePersonalMatam: matamValue,
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !cycleId) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(tr('cands.selectCsvFile', lang));
      return;
    }
    setPendingFile(file);
    setConfirmUploadOpen(true);
    event.target.value = '';
  };

  const handleConfirmUpload = () => {
    if (!pendingFile || !cycleId) return;
    setConfirmUploadOpen(false);
    uploadCandidatesCSVMutation.mutate({ cycleId, file: pendingFile });
    setPendingFile(null);
  };

  const getStatusColor = (status: CandidateStatus) => {
    switch (status) {
      case CandidateStatus.ACTIVE:
        return 'success';
      case CandidateStatus.INACTIVE:
        return 'error';
      case CandidateStatus.GIBUSH_COMPLETED:
        return 'primary';
      case CandidateStatus.GIBUSH_INCOMPLETED:
        return 'warning';
      default:
        return 'default';
    }
  };

  // Flatten candidates from teams structure
  const candidates = cycleStatus?.teams?.flatMap((team: any) => {
    const completedCount = team.candidates?.filter((c: any) => c.status === CandidateStatus.GIBUSH_COMPLETED).length || 0;
    return team.candidates?.map((candidate: any) => ({
      ...candidate,
      teamName: candidate.candidateNumber,
      teamSize: completedCount,
    })) || [];
  }) || [];

  const columns: GridColDef[] = [
    {
      field: 'candidateNumber',
      headerName: tr('cands.col.num', lang),
      width: 130,
      sortable: true,
      filterable: true,
    },
    {
      field: 'teamNumber',
      headerName: tr('cands.col.team', lang),
      width: 130,
      sortable: true,
      filterable: true,
    },
    {
      field: 'status',
      headerName: tr('cands.col.status', lang),
      width: 150,
      sortable: true,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'finalCycleScore',
      headerName: tr('cands.col.cycleScore', lang),
      width: 120,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        params.value != null ? Number(params.value).toFixed(2) : '-'
      ),
    },
    {
      field: 'finalSocioScore',
      headerName: tr('cands.col.socioScore', lang),
      width: 120,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        params.value != null ? Number(params.value).toFixed(2) : '-'
      ),
    },
    {
      field: 'finalAssesmentScore',
      headerName: tr('cands.col.assessScore', lang),
      renderHeader: assessWeights
        ? () => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="MuiDataGrid-columnHeaderTitle">{tr('cands.col.assessScore', lang)}</span>
              <WeightedChip weights={assessWeights} />
            </Box>
          )
        : undefined,
      width: 150,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        params.value != null ? Number(params.value).toFixed(2) : '-'
      ),
    },
    {
      field: 'finalInterviewScore',
      headerName: tr('cands.col.intScore', lang),
      width: 140,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        params.value != null ? Number(params.value).toFixed(2) : '-'
      ),
    },
    {
      field: 'teamleadRank',
      headerName: tr('cands.col.tlRank', lang),
      width: 140,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value != null && params.row.teamSize) {
          return `${params.value}/${params.row.teamSize}`;
        }
        return '-';
      },
    },
    {
      field: 'candidatePersonalMatam',
      headerName: tr('cands.col.matam', lang),
      width: 140,
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams) =>
        params.value != null ? (
          <Chip label={params.value} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, minWidth: 36 }} />
        ) : (
          <Typography component="span" sx={{ color: '#CBD5E1', fontSize: '0.85rem' }}>—</Typography>
        ),
    },
    {
      field: 'redFlag',
      headerName: tr('cands.col.redFlag', lang),
      width: 100,
      sortable: true,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? tr('common.yes', lang) : tr('common.no', lang)}
          color={params.value ? 'error' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'inactivationReason',
      headerName: tr('cands.col.inactReason', lang),
      width: 150,
      sortable: true,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip label={params.value} size="small" variant="outlined" />
        ) : '-'
      ),
    },
    {
      field: 'inactivationReasonText',
      headerName: tr('cands.col.reasonDetails', lang),
      width: 200,
      sortable: true,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        params.value || '-'
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: tr('cands.col.actions', lang),
      width: 100,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label={tr('cands.updateStatus', lang)}
          onClick={() => handleOpenDialog(row)}
          color="inherit"
        />,
      ],
    },
  ];

  if (isLoading) {
    return <LoadingSpinner message={tr('cands.loading', lang)} />;
  }

  const gridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': { bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748B' },
    '& .MuiDataGrid-row:hover': { bgcolor: '#F8FAFC' },
    '& .MuiDataGrid-cell': { borderBottom: '1px solid #F1F5F9', fontSize: '0.875rem' },
    '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E2E8F0' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  };

  return (
    <div>
      {queryError && (
        <ErrorAlert error={queryError} title={tr('cands.errorLoad', lang)} />
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {uploadSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess('')}>
          {uploadSuccess}
        </Alert>
      )}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
          {tr('cd.tab.candidates', lang)}
          <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#94A3B8', fontSize: '0.85rem' }}>
            {candidates.length}
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-upload-input"
            type="file"
            onChange={handleFileUpload}
          />
          <Tooltip title={tr('cands.uploadWarning', lang)} arrow placement="top">
            <label htmlFor="csv-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                size="small"
                disabled={uploadCandidatesCSVMutation.isPending}
              >
                {uploadCandidatesCSVMutation.isPending ? tr('common.uploading', lang) : tr('common.upload', lang)}
              </Button>
            </label>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} size="small" onClick={() => refetch()}>
            {tr('common.refresh', lang)}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
        <DataGrid
          rows={candidates}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={gridSx}
        />
      </Paper>

      {/* CSV Destructive Upload Confirmation */}
      <Dialog open={confirmUploadOpen} onClose={() => { setConfirmUploadOpen(false); setPendingFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          {tr('cands.confirmUploadTitle', lang)}
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {tr('cands.confirmUploadBody', lang)}
          </Alert>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            {tr('cands.confirmUploadFile', lang)}: <strong>{pendingFile?.name}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmUploadOpen(false); setPendingFile(null); }}>{tr('common.cancel', lang)}</Button>
          <Button variant="contained" color="error" onClick={handleConfirmUpload} disabled={uploadCandidatesCSVMutation.isPending}>
            {tr('cands.confirmUploadConfirm', lang)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tr('cands.updateStatus', lang)}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert 
              severity={error.includes('Permission Error') ? 'warning' : 'error'} 
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}
          {selectedCandidate && (
            <Typography variant="h6" sx={{ mb: 2 }}>
              Candidate #{selectedCandidate.candidateNumber} - Team #{selectedCandidate.teamNumber}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{tr('cands.statusLabel', lang)}</InputLabel>
            <Select
              value={formData.status}
              label={tr('cands.statusLabel', lang)}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as CandidateStatus })}
            >
              <MenuItem value={CandidateStatus.ACTIVE}>{tr('common.active', lang)}</MenuItem>
              <MenuItem value={CandidateStatus.INACTIVE}>{tr('common.inactive', lang)}</MenuItem>
              <MenuItem value={CandidateStatus.GIBUSH_COMPLETED}>{tr('cand.gibushCompleted', lang)}</MenuItem>
              <MenuItem value={CandidateStatus.GIBUSH_INCOMPLETED}>{tr('cand.gibushIncompleted', lang)}</MenuItem>
            </Select>
          </FormControl>
          {formData.status === CandidateStatus.INACTIVE && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{tr('cands.inactReasonLabel', lang)}</InputLabel>
                <Select
                  value={formData.inactivationReason || ''}
                  label={tr('cands.inactReasonLabel', lang)}
                  onChange={(e) => setFormData({ ...formData, inactivationReason: e.target.value })}
                >
                  {inactivationReasons?.values.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label={tr('cands.additionalDetails', lang)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={formData.inactivationReasonText || ''}
                onChange={(e) => setFormData({ ...formData, inactivationReasonText: e.target.value })}
              />
            </>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{tr('cands.matamLabel', lang)}</InputLabel>
            <Select
              value={matamValue != null ? String(matamValue) : ''}
              label={tr('cands.matamLabel', lang)}
              onChange={(e) => setMatamValue(e.target.value === '' ? null : Number(e.target.value))}
            >
              <MenuItem value=""><em>{tr('common.notSet', lang)}</em></MenuItem>
              {[1, 2, 3, 4, 5, 6].map(n => (
                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{tr('common.cancel', lang)}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={updateCandidateStatusMutation.isPending}
          >
            {tr('cands.updateStatus', lang)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CandidatesTable;
